import React from 'react'
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { CreatorEarnings } from '../../types'
import Tooltip from '../Tooltip'
import { MONETIZATION_GLOSSARY } from '../../config/glossary'

interface RevenueOverviewProps {
  earnings: CreatorEarnings
  isLoading?: boolean
}

const RevenueOverview: React.FC<RevenueOverviewProps> = ({ earnings, isLoading = false }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const cards: {
    title: string
    hint?: string
    value: string
    icon: typeof CurrencyDollarIcon
    color: string
    description: string
  }[] = [
    {
      title: 'Total Earnings',
      value: formatCurrency(earnings.total_earnings),
      icon: CurrencyDollarIcon,
      color: 'blue',
      description: 'Current month',
    },
    {
      title: 'Available Balance',
      hint: MONETIZATION_GLOSSARY.availableBalance,
      value: formatCurrency(earnings.available_balance),
      icon: BanknotesIcon,
      color: 'green',
      description: 'Ready for payout',
    },
    {
      title: 'Pending Balance',
      hint: MONETIZATION_GLOSSARY.pendingBalance,
      value: formatCurrency(earnings.pending_balance),
      icon: ArrowTrendingUpIcon,
      color: 'yellow',
      description: 'Processing',
    },
    {
      title: 'Active Subscribers',
      value: earnings.active_subscriptions.toString(),
      icon: UserGroupIcon,
      color: 'purple',
      description: `${earnings.total_subscribers} total`,
    },
    {
      title: 'MRR',
      hint: MONETIZATION_GLOSSARY.mrr,
      value: formatCurrency(earnings.monthly_recurring_revenue),
      icon: ArrowTrendingUpIcon,
      color: 'indigo',
      description: 'Monthly recurring revenue',
    },
    {
      title: 'ARPU',
      hint: MONETIZATION_GLOSSARY.arpu,
      value: formatCurrency(earnings.average_revenue_per_user),
      icon: CurrencyDollarIcon,
      color: 'pink',
      description: 'Average revenue per user',
    },
  ]

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; icon: string }> = {
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        icon: 'text-blue-600',
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-600',
        icon: 'text-green-600',
      },
      yellow: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-600',
        icon: 'text-yellow-600',
      },
      purple: {
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        icon: 'text-purple-600',
      },
      indigo: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-600',
        icon: 'text-indigo-600',
      },
      pink: {
        bg: 'bg-pink-100',
        text: 'text-pink-600',
        icon: 'text-pink-600',
      },
    }
    return colors[color] || colors.blue
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {cards.map((card, index) => {
          const Icon = card.icon
          const colors = getColorClasses(card.color)

          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">
                  {card.hint ? <Tooltip content={card.hint}>{card.title}</Tooltip> : card.title}
                </h3>
                <div className={`${colors.bg} p-2 rounded-lg`}>
                  <Icon className={`h-6 w-6 ${colors.icon}`} />
                </div>
              </div>
              <div className="mb-2">
                <p className={`text-2xl font-bold ${colors.text}`}>{card.value}</p>
              </div>
              <p className="text-xs text-gray-500">{card.description}</p>
            </div>
          )
        })}
      </div>

      {earnings.next_payout_date && earnings.next_payout_amount && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Next Payout</h3>
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(earnings.next_payout_amount)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Scheduled for{' '}
                {new Date(earnings.next_payout_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <BanknotesIcon className="h-12 w-12 text-blue-600" />
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          <Tooltip content={MONETIZATION_GLOSSARY.lifetimeEarnings}>Lifetime Earnings</Tooltip>
        </h3>
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-gray-900">
            {formatCurrency(earnings.lifetime_earnings)}
          </span>
          <span className="text-sm text-gray-600">all time</span>
        </div>
      </div>
    </div>
  )
}

export default RevenueOverview
